"use client";
import { JoinButton } from "@cmp-src/components/JoinButton/JoinButton";
import {
  AVATAR_SIZES as USER_PHOTO_SIZES,
  UserAvatar as UserPhotoCard
} from "@cmp-src/components/UserAvatar/UserAvatar";
import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import {
  Collapsible,
  CollapsibleContent
} from "@cmp-src/primitives/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@cmp-src/primitives/dropdown-menu";
import type { ISchedule } from "@gettactic/api";
import { cn, useDisclosure } from "@gettactic/helpers";
import { shouldEnablePrivacyForResourceType } from "@gettactic/helpers/src/resources/utils";
import { canAccessJoinLink } from "@gettactic/helpers/src/schedules/canAccessJoinLink";
import { formatWalkUpOccupancy } from "@gettactic/helpers/src/schedules/formatWalkUpOccupancy";
import { getCdnImageUrl } from "@gettactic/helpers/src/url/getCdnImageUrl";
import { useAuthenticated } from "@gettactic/hooks";
import {
  ChevronDownIcon,
  ClockIcon as ClockOutlineIcon,
  DotsVerticalIcon
} from "@heroicons/react/outline";
import { ClockIcon, PencilIcon, TrashIcon } from "@heroicons/react/solid";
import { isPast } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import React, { useMemo, useState } from "react";
import { useAdminReservationMutations } from "@/features/admin/hooks/useAdminReservationMutations";
import { ExtendReservationDialog } from "@/features/reservation/components/ReservationCard/ExtendReservationDialog";
import { ReservationCancel } from "@/features/reserve/components/ReservationCancel/ReservationCancel";
import { ReserveCheckInButtonAdmin } from "@/features/reserve/components/ReserveCheckin/ReserveCheckInButtonAdmin";
import { ReserveCheckTime } from "@/features/reserve/components/ReserveCheckin/ReserveCheckTime";
import { ReserveEditModal } from "@/features/reserve/components/ReserveEditModal";
import { formatStartEndSlotHours, groupBy } from "@/lib/utils";

type Props = {
  schedules: ISchedule[];
  fullVersion: boolean;
  highlightUserId?: string | undefined;
  isOrgPrivacyEnabled?: boolean;
};

function isRoom(type: string): boolean {
  return type === "meeting_room";
}

function isWorkspace(type: string): boolean {
  return type === "desk" || type === "workspace";
}

export function Groups({
  group,
  shouldCollapse,
  fullVersion,
  highlightUserId,
  isOrgPrivacyEnabled
}: {
  group: ISchedule[];
  shouldCollapse: boolean;
  fullVersion: boolean;
  highlightUserId?: string | undefined;
  isOrgPrivacyEnabled?: boolean;
}) {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const usersInGroup = useMemo(() => {
    const users = group
      .flatMap((s) => s.slots.map((x) => x.user))
      // A walk-up check-in with no real person attached is stamped with a
      // "walkup" placeholder identity — it isn't a real person, so it must
      // not appear as an attendee in this popover.
      .filter((u) => !u.roles?.includes("walkup"));
    // remove duplicates
    return users.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
  }, [group]);

  const isPrivacyEnabled = useMemo(() => {
    if (
      isOrgPrivacyEnabled &&
      shouldEnablePrivacyForResourceType(group[0].resource.type)
    ) {
      const isUserInGroup =
        usersInGroup.findIndex((x) => x.id === authenticatedUser?.user?.id) >
        -1;
      return !isUserInGroup;
    }
    return false;
  }, [authenticatedUser, usersInGroup]);

  const schedule = group[0];
  // Walk-up check-ins have no end time until checkout — treat a missing end
  // as still ongoing (never "in the past") instead of crashing on
  // `null.replace(...)`.
  const endDate = schedule.end ? new Date(schedule.end.replace("Z", "")) : null;
  const scheduleInThePast = !!endDate && isPast(endDate);
  // Meeting rooms only: flag the reservation that is happening right now (we're
  // inside its window and it hasn't been checked out) so we can pulse a dot.
  const startDate = new Date(schedule.start.replace("Z", ""));
  const nowMs = Date.now();
  const isMeetingRoomActiveNow =
    isRoom(schedule.resource.type) &&
    !schedule.has_checked_out &&
    nowMs >= startDate.getTime() &&
    (!endDate || nowMs <= endDate.getTime());
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const extendDisclosure = useDisclosure();
  const { onCheckin, onCheckout, loading, targetSlot } =
    useAdminReservationMutations(schedule, authenticatedUser);
  const [opened, setOpened] = useState(!shouldCollapse);
  const slotForTime = group[0].slots.find(
    (x) => group[0].resource.id === x.resource.id && x.check_out_time
  );
  const hasCheckout = !!slotForTime;
  const officeHasRoomEnableCheckIn =
    authenticatedUser?.offices?.byId[currentOffice.id]
      ?.meeting_room_remote_checkin;
  const officeHasWorkspaceEnableCheckIn =
    authenticatedUser?.offices?.byId[currentOffice.id]
      ?.workspace_remote_checkin;

  const isHighlighted = React.useMemo(() => {
    return highlightUserId
      ? usersInGroup.some((user) => user.id === highlightUserId)
      : false;
  }, [highlightUserId, usersInGroup]);

  const hasAccessToJoinLink = useMemo(
    () => canAccessJoinLink(schedule, authenticatedUser),
    [authenticatedUser, schedule]
  );

  // Attendees render in a collapsible list — only worth a toggle when there's a list to hide.
  const hasAttendeeList = !isPrivacyEnabled && usersInGroup.length > 0;

  // Attendees are shown as the organizer on top with the other guests
  // grouped below. A walk-up check-in with no real person attached is
  // stamped with a "walkup" placeholder identity — not a real organizer.
  const organizer = schedule.organizer?.roles?.includes("walkup")
    ? undefined
    : schedule.organizer;
  const guests = usersInGroup.filter((user) => user.id !== organizer?.id);

  // `check_in_time` is a real UTC instant (unlike `start`/`end`, which
  // carry a trailing `Z` but are actually wall-clock) — converted to the
  // schedule's own office timezone rather than the viewer's browser, or
  // this reads wrong for anyone not physically in that office's timezone.
  const walkUpCheckInTimeStr = group[0].slots.find(
    (x) => x.check_in_time
  )?.check_in_time;
  const walkUpCheckInTime = walkUpCheckInTimeStr
    ? toZonedTime(new Date(walkUpCheckInTimeStr), group[0].time_zone)
    : null;
  // "Today" must be the office's today, not the viewer's — computed the
  // same way as walkUpCheckInTime so the two compare fairly.
  const walkUpNowInOfficeTime = toZonedTime(new Date(), group[0].time_zone);

  return (
    <div
      className={cn(
        "mt-1.5 flex flex-col rounded-lg border border-neutral-200/50 bg-[#fbfcfd] px-3",
        isHighlighted && "border-primary-500 ring-1 ring-primary-500"
      )}
    >
      <div className="flex h-[42px] items-center gap-2 text-left text-neutral-900 text-sm">
        {!isPrivacyEnabled &&
        usersInGroup.length === 1 &&
        usersInGroup[0] &&
        shouldCollapse ? (
          <div
            className={cn("h-5 w-5 shrink-0", {
              grayscale: usersInGroup[0].roles.includes("deleted")
            })}
          >
            <UserPhotoCard
              size={USER_PHOTO_SIZES.ROUNDED_FULL_SM}
              profile_photo_url={
                usersInGroup[0].profile_photo_url
                  ? getCdnImageUrl(
                      usersInGroup[0].profile_photo_url,
                      "40x40_face"
                    )
                  : null
              }
              name={
                usersInGroup[0].name +
                (usersInGroup[0].roles.includes("deleted") ? " (inactive)" : "")
              }
            />
          </div>
        ) : undefined}
        <button
          type="button"
          onClick={() => setOpened((o) => !o)}
          className="flex min-w-0 grow items-center gap-1.5 self-stretch text-left font-semibold"
        >
          <ClockOutlineIcon
            className="h-4 w-4 shrink-0 text-[#2e9266]"
            aria-hidden="true"
          />
          <span className="font-inter min-w-0 truncate font-semibold text-neultral-1000 text-[13.5px]">
            {group[0].resource.is_walkup && !group[0].end
              ? // No end yet (still checked in, not checked out) — there's
                // no window to show a range for.
                formatWalkUpOccupancy(walkUpCheckInTime, walkUpNowInOfficeTime)
              : formatStartEndSlotHours(group[0], " → ")}
            {shouldCollapse && group.length > 1
              ? ` (${group.length} attendees)`
              : null}
          </span>
          {isMeetingRoomActiveNow ? (
            <span
              className="relative flex h-[7px] w-[7px] shrink-0"
              role="status"
              aria-label="In progress"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75 [animation-duration:1.8s]" />
              <span className="relative inline-flex h-[7px] w-[7px] rounded-full bg-red-500" />
            </span>
          ) : null}
          {hasAttendeeList ? (
            <ChevronDownIcon
              className={cn(
                "h-4 w-4 shrink-0 text-[#9aa4ab] transition-transform duration-[160ms]",
                opened && "rotate-180"
              )}
              aria-hidden="true"
            />
          ) : null}
        </button>
        <JoinButton
          onlineMeeting={schedule.online_meeting ?? null}
          iconOnly={true}
          hasAccess={hasAccessToJoinLink}
          wrapper={(children, icon, props) => (
            <button
              onClick={props.onClick}
              className="inline-flex items-center justify-center h-5 w-5 text-tertiary hover:text-primary disabled:opacity-50"
              disabled={props.disabled}
            >
              {icon}
            </button>
          )}
        />
        {targetSlot && fullVersion ? (
          <>
            {/* Walk-up occupancy already reads "Occupied since HH:MM" above
                (formatWalkUpOccupancy) — showing "Checked in at" here too is
                redundant, and check-in/out for a walk-up happens externally,
                never through this button. */}
            {!targetSlot.resource.is_walkup &&
            (targetSlot.capabilities.can_respond_presence_check_in ||
              targetSlot.capabilities.can_respond_presence_check_out ||
              targetSlot.check_out_time) &&
            ((isWorkspace(targetSlot.resource.type) &&
              officeHasWorkspaceEnableCheckIn &&
              !schedule.is_pending) ||
              (isRoom(targetSlot.resource.type) &&
                officeHasRoomEnableCheckIn &&
                !schedule.is_pending)) ? (
              <ReserveCheckInButtonAdmin
                targetSlot={targetSlot}
                loading={loading}
                onCheckin={onCheckin}
                onCheckout={onCheckout}
                schedule={schedule}
                size="sm"
                className="h-7 px-2.5 text-xs"
              />
            ) : null}
            {targetSlot.capabilities.can_edit ||
            targetSlot.capabilities.can_respond_cancelled ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="inline-flex items-center justify-center">
                    <DotsVerticalIcon className="h-5 w-5 cursor-pointer text-tertiary" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  withPortal={false}
                  align="end"
                  className="w-[200px]"
                >
                  <DropdownMenuItem
                    onSelect={() => {
                      setEditModal(true);
                    }}
                    disabled={!targetSlot.capabilities.can_edit}
                    className={
                      !targetSlot.capabilities.can_edit
                        ? "text-neutral-300"
                        : ""
                    }
                  >
                    <PencilIcon
                      className={`mr-2 h-5 w-5 ${targetSlot.capabilities.can_edit ? "text-tertiary" : "text-disabled"}`}
                    />
                    Edit Reservation
                  </DropdownMenuItem>

                  {schedule.has_checked_in &&
                    !schedule.has_checked_out &&
                    !scheduleInThePast &&
                    // Nothing to extend without a defined end (a walk-up
                    // check-in has none until checkout).
                    endDate && (
                      <DropdownMenuItem onSelect={extendDisclosure.open}>
                        <ClockIcon className="mr-2 h-5 w-5 text-tertiary" />
                        Extend Reservation
                      </DropdownMenuItem>
                    )}

                  <DropdownMenuItem
                    disabled={!targetSlot.capabilities.can_respond_cancelled}
                    onSelect={() => setConfirmDialog(true)}
                  >
                    <TrashIcon
                      className={`mr-2 h-5 w-5 ${targetSlot.capabilities.can_respond_cancelled ? "text-tertiary" : "text-disabled"}`}
                    />
                    Cancel Reservation
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
          </>
        ) : hasCheckout ? (
          <span className="shrink-0 whitespace-nowrap rounded-full border border-tacticGray-400 bg-white px-2 py-1 text-center">
            <ReserveCheckTime
              time={slotForTime.check_out_time}
              type={"checkout"}
              clsNames={{
                general: "tactic-textGray-400 font-normal",
                time: "text-primary"
              }}
            />
          </span>
        ) : null}
      </div>
      {!isPrivacyEnabled ? (
        <Collapsible open={opened} onOpenChange={setOpened}>
          <CollapsibleContent>
            <div className="-mx-1.5 border-neutral-200/50 border-t px-1.5 pt-2.5 pb-2.5">
              <div className="flex flex-col gap-2">
                {organizer ? (
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <UserAvatar
                        user={organizer}
                        size={"s"}
                        className="h-[26px] w-[26px]"
                      />
                      <span className="font-inter truncate text-neutral-1000 font-semibold text-[13px]">
                        {organizer.name}
                      </span>
                    </div>
                    {guests.length > 0 ? (
                      <span className="font-inter shrink-0 text-[#9aa4ab] text-[11.5px]">
                        Organizer
                      </span>
                    ) : null}
                  </div>
                ) : null}
                {guests.length > 0 ? (
                  <div className="flex items-center gap-2">
                    <UserAvatarStack users={guests} restVariant="badge" />
                    <span className="font-inter text-neutral-500 text-xs">
                      {guests.length} {guests.length === 1 ? "guest" : "guests"}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
      <ReservationCancel
        schedules={[schedule]}
        setOpen={setConfirmDialog}
        open={confirmDialog}
      />
      <ReserveEditModal
        schedule={schedule}
        editModal={editModal}
        toggleModal={() => setEditModal(!editModal)}
      />
      <ExtendReservationDialog
        isOpen={extendDisclosure.isOpen}
        onOpenChange={extendDisclosure.onOpenChange}
        onClose={extendDisclosure.close}
        schedule={schedule}
      />
    </div>
  );
}
export function SchedulesGroups({
  schedules,
  fullVersion,
  highlightUserId,
  isOrgPrivacyEnabled
}: Props) {
  const groups = Object.values(
    groupBy(schedules, (s: ISchedule) => `${s.resource.id}-${s.id}`)
  );
  // we want to collapse by default if are more than one group or if
  // the group has more than 4 members
  const shouldCollapse = groups.length > 1 || groups[0].length > 4;
  return (
    <div className="max-h-36 w-full overflow-y-auto overflow-x-hidden">
      {groups.map((group) => {
        return (
          <Groups
            key={group[0].id}
            group={group}
            shouldCollapse={shouldCollapse}
            fullVersion={fullVersion}
            highlightUserId={highlightUserId}
            isOrgPrivacyEnabled={isOrgPrivacyEnabled}
          />
        );
      })}
    </div>
  );
}
